/**
 * icons.js — the single semantic icon registry for the whole application.
 *
 * WHY A REGISTRY, AND NOT `import { LuTrash2 } from 'react-icons/lu'` AT EACH SITE
 *
 *  1. ONE DOOR, ENFORCED. v1 imported from five different react-icons packs —
 *     including BOTH Ionicons generations (`io` and `io5`) in the same screen —
 *     so a delete button in one list was a thin outline and the delete button
 *     in the next list was a filled glyph at a different optical weight. Every
 *     `react-icons` import in `src/` is in THIS FILE; a screen cannot reach a
 *     pack at all. `lib/__tests__/icon-registry.test.js` fails the build if one
 *     appears anywhere else, or if a pack this file does not declare turns up.
 *
 *     The application is Lucide throughout, with ONE declared exception —
 *     `CloseIcon` — noted where it is imported below. An exception recorded in
 *     one file is a decision; the same glyph pulled from a second pack inside
 *     a screen is the drift that cost v1 its consistency.
 *
 *  2. ONE MEANING, ONE GLYPH. Screens ask for `DeleteIcon`, not `LuTrash2`.
 *     When two screens both want "archive", they cannot accidentally pick
 *     `LuArchive` and `LuBox`, because neither name is reachable from a screen.
 *     A credit note is `SalesReturnIcon` in the list, on the dashboard and in
 *     its badge — the same import, so the same glyph, by construction.
 *
 *  3. RESKINNING IS ONE EDIT. Swapping the whole app from Lucide to Bootstrap
 *     Icons, or changing what "payment" looks like, is a change to the right
 *     hand side of one line here rather than a repo-wide find-and-replace over
 *     forty files that would inevitably miss some.
 *
 *  4. NAMES SURVIVE THE VENDOR. `LuMoreVertical` became `LuEllipsisVertical`
 *     in react-icons v5. A rename like that is one line here instead of a
 *     broken build in every list screen.
 *
 * SIZING
 * Use the `ICON_SIZE` scale, not ad-hoc numbers. The `Button`, `MenuItem`,
 * `Stat`, `Alert` and `EmptyState` components already size the icon they are
 * handed, so pass the component itself (`icon={AddIcon}`) wherever a component
 * accepts one, and only set `size` on a bare `<Icon />` rendered inline.
 *
 * ACCESSIBILITY
 * Every icon here is decorative. Next to a visible text label it must carry
 * `aria-hidden="true"`; a control with no visible label must carry its own
 * `aria-label`. An icon never replaces a label.
 */

/**
 * THE ONE DELIBERATE EXCEPTION TO "ONE PACK".
 *
 * `CloseIcon` is Ionicons' outline cross rather than Lucide's `LuX`, by
 * explicit request: at the size these buttons run, Lucide's square-capped X
 * reads as two hard strokes and the Ionicons one is rounder and lighter.
 *
 * WHAT THIS COSTS, so that whoever reads this next can weigh it. A cross drawn
 * from a different pack sits beside Lucide glyphs everywhere it appears — next
 * to the trash icon in a list row, next to the chevron in a picker — and the
 * two are drawn on different grids at different stroke weights. That is the
 * exact drift the header above describes v1 suffering from. It is accepted
 * here for ONE glyph, chosen on how it looks in place.
 *
 * The rule that still holds without exception: screens never import a pack.
 * They ask for `CloseIcon`, so which pack draws it is this file's decision and
 * changing it back is one line.
 */
import { IoCloseOutline } from 'react-icons/io5';

/* Lucide — every other glyph in the application. */
import {
  LuClipboardCheck,
  LuClipboardList,
  LuCreditCard,
  LuFileQuestion,
  LuFileSpreadsheet,
  LuFileText,
  LuReceipt,
  LuRotateCcw,
  LuRotateCw,
  LuScrollText,
  LuTruck,
} from 'react-icons/lu';
import { VOUCHER_TYPES } from '../../lib/voucher-types';

/* ------------------------------------------------------------------ *
 * Size scale
 *
 * Five steps, no others. `xs` is for the inside of a 19px badge pill, `sm`
 * matches a 13px label inside a small control, `md` is the default beside body
 * text, `lg` is a section or feature marker, `xl` is the glyph at the top of an
 * empty state.
 * ------------------------------------------------------------------ */

export const ICON_SIZE = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

/* ================================================================== *
 * DOMAIN — the nouns this application is about
 * ================================================================== */

export {
  /* Documents ---------------------------------------------------- */
  LuFileText as InvoiceIcon, // a sales invoice / tax invoice
  LuFilePlus as NewInvoiceIcon, // "raise a new document"
  LuReceipt as BillIcon, // a purchase bill
  LuRotateCcw as CreditNoteIcon, // sales return — goods come back to us
  LuRotateCw as DebitNoteIcon, // purchase return — goods go back to them
  LuCreditCard as ExpenseIcon,
  LuScrollText as VoucherIcon, // a voucher of unspecified type
  LuClipboardList as LineItemsIcon,

  /* People ------------------------------------------------------- */
  LuUsers as PartyIcon,
  LuUser as CustomerIcon,
  LuTruck as SupplierIcon,
  LuUserPlus as AddPartyIcon,
  LuBuilding2 as BusinessIcon,

  /* Goods -------------------------------------------------------- */
  LuPackage as ItemIcon,
  LuBoxes as StockIcon,
  LuPackagePlus as AdjustStockIcon,
  LuLayers as BatchIcon,
  LuTags as CategoryIcon,
  LuRuler as UnitIcon,

  /* Money -------------------------------------------------------- */
  LuIndianRupee as MoneyIcon,
  LuWallet as PaymentIcon,
  LuHandCoins as ReceiptIcon, // money coming in
  LuBanknote as PayoutIcon, // money going out
  LuLandmark as BankIcon,
  LuScale as BalanceIcon,
  LuArrowDownLeft as InflowIcon, // received / receivable
  LuArrowUpRight as OutflowIcon, // paid / payable

  /* Tax ---------------------------------------------------------- */
  LuPercent as TaxIcon,
  LuBadgePercent as GstIcon,
  LuFileCheck as ReturnFilingIcon,

  /* Reporting ---------------------------------------------------- */
  LuChartNoAxesColumn as ReportIcon,
  LuNotebookText as LedgerIcon,
  LuHistory as ActivityIcon,
  LuTrendingUp as TrendUpIcon,
  LuTrendingDown as TrendDownIcon,
  LuLayoutDashboard as DashboardIcon,

  /* The books -----------------------------------------------------
     A register of entries: the Day Book, and the four accounting registers
     (Payments, Receipts, Contra, Journal) that feed it. One noun, one glyph —
     they are the same kind of thing and they sit next to each other. */
  LuBookOpen as BookIcon,
  LuLibrary as ChartOfAccountsIcon,
  LuArrowRightLeft as CashFlowIcon, // money moving between heads over a period
  LuCreditCard as OutstandingIcon, // what is owed, either way
  LuWarehouse as WarehouseIcon,
  LuClipboardCheck as StockTakeIcon, // counting the shelf, not an order
} from 'react-icons/lu';

/* ================================================================== *
 * ACTIONS — the verbs a control performs
 * ================================================================== */

export {
  LuPlus as AddIcon,
  /* One fewer of something already on the document — the counter's quantity
     stepper and the shop window's. NOT `CloseIcon`: removing the line
     altogether is a different action with a different glyph. */
  LuMinus as MinusIcon,
  LuRotateCcw as ResetIcon, // put a control back to its default
  LuPencil as EditIcon,
  LuTrash2 as DeleteIcon,
  LuArchive as ArchiveIcon,
  LuArchiveRestore as RestoreIcon,
  LuSave as SaveIcon,
  LuCopy as CopyIcon,
  LuCopyPlus as DuplicateIcon,
  LuPrinter as PrintIcon,
  LuDownload as DownloadIcon,
  LuUpload as UploadIcon,
  LuImagePlus as ImageIcon,
  LuSearch as SearchIcon,
  LuFilter as FilterIcon,
  LuCalendar as CalendarIcon,
  LuRefreshCw as RefreshIcon,
  LuBan as CancelIcon, // void a document — NOT "close a dialog"
  LuCheck as CheckIcon,
  LuEye as ViewIcon,
  LuUndo2 as ReturnIcon, // raise a return against this document
  LuSettings as SettingsIcon,
  LuLogOut as SignOutIcon,
  LuZap as QuickActionIcon,
  LuWrench as MaintenanceIcon,
  LuShare2 as ShareIcon, // hand the document to another app
  LuFileQuestion as QuotationIcon,
  LuFileSpreadsheet as ProformaIcon,
  LuClipboardCheck as OrderIcon,
  LuTruck as ChallanIcon,
  LuArrowRightLeft as ConvertIcon, // turn one document into another
  LuCircleCheckBig as CloseOrderIcon,
  LuQrCode as QrIcon,
  LuScanLine as ScanIcon, // read a barcode
  LuLink as LinkIcon,
} from 'react-icons/lu';

/**
 * Dismiss / clear / remove-this-row.
 *
 * Ionicons rather than Lucide — see the note on the import at the top of this
 * file. Kept as a named export like every other icon, so no screen knows or
 * cares which pack it came from.
 */
export { IoCloseOutline as CloseIcon };

/* ================================================================== *
 * NAVIGATION AND AFFORDANCES
 * ================================================================== */

export {
  LuEllipsisVertical as MoreIcon,
  /* `ChevronIcon` is the EXPAND / COLLAPSE affordance, everywhere. It points
     down when a thing is open and is rotated by CSS when it is closed, so one
     glyph covers both states — a separate "collapse" icon would be a second
     thing to keep in sync with the first. The select's caret, the sidebar's
     section headings and every disclosure use this one. */
  LuChevronDown as ChevronIcon,
  LuChevronRight as ChevronRightIcon,
  /* Open or close EVERY branch of a tree at once. Chevrons apart to open,
     chevrons together to close — a pair that reads as one control in two
     states, which is what it is. */
  LuChevronsUpDown as ExpandAllIcon,
  LuChevronsDownUp as CollapseAllIcon,
  LuArrowLeft as BackIcon,
  LuArrowRight as NextIcon,
  LuExternalLink as ExternalIcon,
  LuGripVertical as DragIcon,
  LuMenu as MenuIcon, // open the navigation drawer on a narrow screen
} from 'react-icons/lu';

/* ================================================================== *
 * STATUS AND FEEDBACK
 * ================================================================== */

export {
  LuTriangleAlert as WarningIcon,
  LuCircleCheck as SuccessIcon,
  LuInfo as InfoIcon,
  LuCircleAlert as ErrorIcon,
  LuClock as PendingIcon,
  LuCalendarClock as OverdueIcon,
  LuCircleHelp as HelpIcon,
  LuLock as LockedIcon,
  /* The connection. Two glyphs and not one with a strikethrough class,
     because "connected" and "working offline" are states a user acts on, and
     the control that changes them shows the state it is currently in. */
  LuCloud as OnlineIcon,
  LuCloudOff as OfflineIcon,
} from 'react-icons/lu';

/* ================================================================== *
 * EMPTY STATES
 *
 * Deliberately several, not one. "No invoices yet", "nothing matched your
 * search" and "this ledger has no entries" are three different situations and
 * the same inbox glyph on all three tells the reader nothing. The generic
 * `EmptyIcon` is the fallback for a list with no better noun.
 * ================================================================== */

export {
  LuInbox as EmptyIcon,
  LuSearchX as NoResultsIcon,
  LuMapPinOff as NotFoundIcon,
  LuFileSearch as NoDocumentsIcon,
} from 'react-icons/lu';

/* ================================================================== *
 * SETTINGS, AUTH AND ODDMENTS
 * ================================================================== */

export {
  LuMonitor as SystemThemeIcon,
  LuSun as LightThemeIcon,
  LuMoon as DarkThemeIcon,
  LuBellRing as ReminderIcon,
  LuSmartphone as PhoneIcon,
  LuMessageSquare as SmsIcon,
  LuMail as EmailIcon,
  LuMapPin as AddressIcon,
  LuStickyNote as NoteIcon,
  LuHash as NumberingIcon,
  LuDatabase as DataIcon,
  LuShieldCheck as SecurityIcon,
  LuPalette as AppearanceIcon,
  LuTag as PriceListIcon, // what a thing costs THIS customer
  LuUsersRound as TeamIcon, // the people who can sign in
  LuUserCog as RoleIcon,
  LuFactory as ManufacturingIcon,
  LuStore as StoreIcon,
  LuRepeat as RepeatIcon, // a document that raises itself on a schedule
  LuSend as SendIcon, // the outbox — messages actually dispatched
} from 'react-icons/lu';

/* ================================================================== *
 * VOUCHER TYPE → ICON
 *
 * The one place that decides what each voucher type looks like. A list row, a
 * dashboard tile and a type badge all read this map, so they cannot disagree.
 * Keyed off VOUCHER_TYPES rather than string literals so a renamed type is a
 * build error here rather than a silently missing icon on three screens.
 *
 * These are the same bindings the named exports above alias, so
 * `VOUCHER_TYPE_ICONS[SALES_RETURN] === CreditNoteIcon` holds by construction —
 * the credit note in a list row, on a stat tile and in a badge cannot drift.
 * ================================================================== */

export const VOUCHER_TYPE_ICONS = {
  [VOUCHER_TYPES.SALES]: LuFileText,
  [VOUCHER_TYPES.SALES_RETURN]: LuRotateCcw,
  [VOUCHER_TYPES.PURCHASE]: LuReceipt,
  [VOUCHER_TYPES.PURCHASE_RETURN]: LuRotateCw,
  [VOUCHER_TYPES.EXPENSE]: LuCreditCard,

  /* The non-posting documents. Each gets its own glyph rather than sharing the
     generic one: a sidebar with five identical icons is a sidebar nobody scans,
     and these five sit next to each other. */
  [VOUCHER_TYPES.QUOTATION]: LuFileQuestion,
  [VOUCHER_TYPES.PROFORMA]: LuFileSpreadsheet,
  [VOUCHER_TYPES.SALES_ORDER]: LuClipboardCheck,
  [VOUCHER_TYPES.DELIVERY_CHALLAN]: LuTruck,
  [VOUCHER_TYPES.PURCHASE_ORDER]: LuClipboardList,
};

/** Never returns undefined: an unknown or legacy type still gets a glyph. */
export function voucherTypeIcon(type) {
  return VOUCHER_TYPE_ICONS[type] || LuScrollText;
}
